<script setup lang="ts">
import { ref, computed, useSeamlessWalletAccountDetails, useFormat, useGuard, useGtag, useI18n, useTracking, navigateTo } from '#imports'

const emit = defineEmits<{
    click: [event: MouseEvent]
}>()

const { asMoney } = useFormat()
const { can } = useGuard()
const { gtag } = useGtag()
const { t } = useI18n()
const { trackEvent } = useTracking()
const { data: accountDetails } = useSeamlessWalletAccountDetails()

const userBalanceLoading = ref(false)

const userBalance = computed(() => {
    return Number(accountDetails.value?.balance || 0)
})

async function handleClick(event: MouseEvent) {
    trackEvent({
        event_name: 'top-nav_deposit_click',
        event_type: 'Click',
        meta_data: [{ category: 'Navigation' }],
    })

    const isAllowed = await can()

    if (!isAllowed) {
        event.preventDefault()

        return
    }

    emit('click', event)

    gtag('event', 'deposit_click')

    userBalanceLoading.value = true
    await navigateTo(t('pages.deposit'))
}
</script>

<template>
    <UIButton
        as="link"
        size="sm"
        :center-text="false"
        class="deposit-button"
        data-test="deposit-button"
        @click="handleClick"
    >
        <template #prepend>
            <Transition name="fade">
                <UIIcon
                    v-if="accountDetails?.balance && !userBalanceLoading"
                    name="plus"
                    size="18"
                />
            </Transition>
        </template>

        <Transition name="fade">
            <span
                v-if="accountDetails?.balance && !userBalanceLoading"
                class="deposit-button__balance"
            >
                {{ asMoney(userBalance) }}
            </span>

            <UILoader
                v-else
                key="loader"
                class="deposit-button__balance--loader"
                width="36px"
                height="36px"
            />
        </Transition>
    </UIButton>
</template>

<style scoped lang="scss">
.btn.btn--link.deposit-button {
    display: flex;
    justify-items: center;
    height: 36px;
    border-radius: $border-radius * 3;
    box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.15);
    background-color: var(--surface);
    color: var(--on-background);
    min-width: 60px;
    transition:
        box-shadow 0.2s ease-in-out,
        width 0.2s ease-in-out,
        background-color 0.2s ease-in-out;

    :deep(.icon) {
        background-color: var(--primary);
        color: var(--on-primary);
        padding: 4px;
        border-radius: 50%;
        margin-left: -4px;
        min-width: 18px;
        min-height: 18px;
    }

    :deep(.loader) {
        margin-left: 0;
    }

    &:hover {
        @include nav-action-hover;
    }

    &:hover,
    &:active,
    &:focus,
    &:visited {
        text-decoration: none;
        color: var(--on-background);
    }

    &__balance {
        font-weight: $text-semi-bold;
        display: flex;
        align-items: center;
        gap: 6px;
    }
}
</style>
